import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { defaultLocale, getSiteCopy, localeMeta, type Locale } from "./siteCopy";
import { buildLocalizedPath, parseSitePath } from "../lib/siteRouting";

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  copy: ReturnType<typeof getSiteCopy>;
  locales: typeof localeMeta;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

const storageKey = "qiandu-site-locale";
const originUrl = "https://www.qiandu.group";

function syncUrl(locale: Locale, replace = false) {
  const { contentPath } = parseSitePath(window.location.pathname);
  const targetPath = buildLocalizedPath(locale, contentPath === "/" ? "/" : contentPath);
  const currentPath = window.location.pathname === "/" ? "/" : window.location.pathname.replace(/\/+$/, "");
  if (currentPath === targetPath) return;
  const method = replace ? "replaceState" : "pushState";
  window.history[method]({}, "", `${targetPath}${window.location.hash}`);
}

function updateMeta(name: string, content: string, attribute: "name" | "property" = "name") {
  const selector = `meta[${attribute}="${name}"]`;
  const element = document.head.querySelector(selector);
  if (element) {
    element.setAttribute("content", content);
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(defaultLocale);

  useEffect(() => {
    const parsed = parseSitePath(window.location.pathname);
    const pathLocale = parsed.locale;
    const saved = window.localStorage.getItem(storageKey) as Locale | null;
    const nextLocale =
      (pathLocale && pathLocale in localeMeta ? pathLocale : null) ??
      (saved && saved in localeMeta ? saved : defaultLocale);

    setLocale(nextLocale);

    if (window.location.pathname === "/") {
      syncUrl(nextLocale, true);
    }

    const handlePopState = () => {
      const popLocale = parseSitePath(window.location.pathname).locale ?? defaultLocale;
      setLocale(popLocale);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(storageKey, locale);
    document.documentElement.lang = locale;
    document.documentElement.dataset.locale = locale;
    syncUrl(locale);

    const copy = getSiteCopy(locale);
    const { contentPath } = parseSitePath(window.location.pathname);
    const canonical = `${originUrl}${buildLocalizedPath(locale, contentPath)}`;

    document.title = copy.seo.title;
    updateMeta("description", copy.seo.description);
    updateMeta("og:title", copy.seo.ogTitle, "property");
    updateMeta("og:description", copy.seo.ogDescription, "property");
    updateMeta("og:url", canonical, "property");
    updateMeta("twitter:title", copy.seo.ogTitle);
    updateMeta("twitter:description", copy.seo.ogDescription);

    const canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonical);
    }
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      copy: getSiteCopy(locale),
      locales: localeMeta,
    }),
    [locale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return context;
}
